<template>
    <div
        id="sidebar-container"
    >
        <!-- Mobile Overlay -->
        <transition name="fade">
            <div
                v-if="isSidebarOpen"
                class="fixed inset-0 bg-gray-900 bg-opacity-50 z-40 lg:hidden"
                @click="toggleSidebar(false)"
            ></div>
        </transition>

        <!-- Sidebar -->
        <div
            :class="[
                'fixed flex flex-col top-0 left-0 z-40 bg-white h-full border-r shadow-sm transition-all duration-300',
                isCollapsed ? 'w-20' : 'w-64',
            ]"
            :style="{ transform: isMobile && !isSidebarOpen ? 'translateX(-100%)' : 'translateX(0)' }"
        >
                <!-- Header with Logo and Toggle -->
                <div class="flex items-center justify-between h-16 border-b px-4 bg-white">
                    <router-link to="/dashboard" class="flex items-center">
                        <img
                            v-if="!isCollapsed"
                            class="h-10 transition-all duration-300"
                            src="../assets/mainlogo.jpg"
                        />
                        <div v-else class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <i class="bx bx-home text-indigo-600 text-xl"></i>
                        </div>
                    </router-link>
                    <!-- Sidebar Toggle Button Deployment -->
                    <button
                        @click="handleToggleClick"
                        class="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-200 transition-colors text-gray-600"
                        title="Toggle sidebar"
                    >
                        <!-- On mobile show X to close, on desktop show chevron -->
                        <i v-if="isMobile" class="bx bx-x text-xl"></i>
                        <i v-else :class="isCollapsed ? 'bx bx-chevron-right' : 'bx bx-chevron-left'" class="text-xl"></i>
                    </button>
                </div>

                <!-- Navigation Menu -->
                <div class="flex-grow overflow-y-auto overflow-x-hidden custom-scrollbar" >
                    <ul class="flex flex-col py-4 space-y-1">
                        <!-- Dashboard -->
                        <li v-if="checkPermissionExist(user.data, 'dashboard-read')">
                            <router-link
                                to="/dashboard"
                                :class="getMenuItemClass('/dashboard')"
                                :title="isCollapsed ? 'Dashboard' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-home text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Dashboard</span>
                            </router-link>
                        </li>

                        <!-- Deposits -->
                        <li v-if="checkPermissionExist(user.data, 'deposit-read')">
                            <router-link
                                to="/deposits"
                                :class="getMenuItemClass('/deposits')"
                                :title="isCollapsed ? 'Deposits' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-money text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Deposits</span>
                            </router-link>
                        </li>

                        <!-- Transactions -->
                        <li v-if="checkPermissionExist(user.data, 'transaction-read')">
                            <router-link
                                to="/new_transactions"
                                :class="getMenuItemClass('/new_transactions')"
                                :title="isCollapsed ? 'Transactions' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-transfer text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Transactions</span>
                            </router-link>
                        </li>

                        <!-- Branch Transactions (alternative) -->
                        <li v-else>
                            <router-link
                                to="/branch_transactions"
                                :class="getMenuItemClass('/branch_transactions')"
                                :title="isCollapsed ? 'Transactions' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-transfer text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Transactions</span>
                            </router-link>
                        </li>

                        <!-- Manage Float -->
                        <li v-if="checkPermissionExist(user.data, 'floats-read')">
                            <router-link
                                to="/manage_floats"
                                :class="getMenuItemClass('/manage_floats')"
                                :title="isCollapsed ? 'Manage Float' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-bookmark-plus text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Manage Float</span>
                            </router-link>
                        </li>

                        <!-- Commission Center -->
                        <li v-if="checkPermissionExist(user.data, 'floats-read')">
                            <router-link
                                to="/commission_center"
                                :class="getMenuItemClass('/commission_center')"
                                :title="isCollapsed ? 'Commission Center' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-dollar-circle text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Commission Center</span>
                            </router-link>
                        </li>

                        <!-- Worksheets -->
                        <li v-if="checkPermissionExist(user.data, 'worksheets-read')">
                            <router-link
                                to="/worksheets"
                                :class="getMenuItemClass('/worksheets')"
                                :title="isCollapsed ? 'Worksheets' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-briefcase-alt text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Worksheets</span>
                            </router-link>
                        </li>

                        <!-- Divider -->
                        <li v-if="!isCollapsed" class="px-5 pt-4">
                            <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                Operations
                            </div>
                        </li>
                        <li v-else class="border-t border-gray-200 my-2"></li>

                        <!-- Cash Collection -->
                        <li v-if="checkPermissionExist(user.data, 'cashcollection-read')">
                            <router-link
                                to="/cashcollection"
                                :class="getMenuItemClass('/cashcollection')"
                                :title="isCollapsed ? 'Cash Collection' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-collection text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Cash Collection</span>
                            </router-link>
                        </li>

                        <!-- Branch Cash Collection -->
                        <li v-if="checkPermissionExist(user.data, 'cashcollection-branch-read')">
                            <router-link
                                to="/branch_cashcollection"
                                :class="getMenuItemClass('/branch_cashcollection')"
                                :title="isCollapsed ? 'Branch Cash Collection' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-collection text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Branch Cash Collection</span>
                            </router-link>
                        </li>

                        <!-- Reconciliations -->
                        <li v-if="checkPermissionExist(user.data, 'reconciliations-read')">
                            <router-link
                                to="/reconciliations"
                                :class="getMenuItemClass('/reconciliations')"
                                :title="isCollapsed ? 'Reconciliations' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-clipboard text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Reconciliations</span>
                            </router-link>
                        </li>

                        <!-- NAWEC Recovery -->
                        <li v-if="checkPermissionExist(user.data, 'reconciliations-read')">
                            <router-link
                                to="/nawec-reconciliation"
                                :class="getMenuItemClass('/nawec-reconciliation')"
                                :title="isCollapsed ? 'NAWEC Recovery' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-analyse text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">NAWEC Recovery</span>
                            </router-link>
                        </li>

                        <!-- Divider -->
                        <li v-if="!isCollapsed" class="px-5 pt-4">
                            <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                Management
                            </div>
                        </li>
                        <li v-else class="border-t border-gray-200 my-2"></li>

                        <!-- Agents -->
                        <li v-if="checkPermissionExist(user.data, 'agents-read')">
                            <router-link
                                to="/agents"
                                :class="getMenuItemClass('/agents')"
                                :title="isCollapsed ? 'Agents' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-group text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Agents</span>
                            </router-link>
                        </li>

                        <!-- Super Agents -->
                        <li v-if="checkPermissionExist(user.data, 'agents-read')">
                            <router-link
                                to="/superagents"
                                :class="getMenuItemClass('/superagents')"
                                :title="isCollapsed ? 'Super Agents' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-user-check text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Super Agents</span>
                            </router-link>
                        </li>

                        <!-- Agent Transactions -->
                        <li>
                            <router-link
                                to="/agent_transactions"
                                :class="getMenuItemClass('/agent_transactions')"
                                :title="isCollapsed ? 'Agent Transactions' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-transfer-alt text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Agent Transactions</span>
                            </router-link>
                        </li>

                        <!-- Staffs -->
                        <li v-if="checkPermissionExist(user.data, 'staffs-read')">
                            <router-link
                                to="/staffs"
                                :class="getMenuItemClass('/staffs')"
                                :title="isCollapsed ? 'Staffs' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-user text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Staffs</span>
                            </router-link>
                        </li>

                        <!-- Branches -->
                        <li v-if="checkPermissionExist(user.data, 'branchs-read')">
                            <router-link
                                to="/branches"
                                :class="getMenuItemClass('/branches')"
                                :title="isCollapsed ? 'Branches' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-buildings text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Branches</span>
                            </router-link>
                        </li>

                        <!-- Divider -->
                        <li v-if="!isCollapsed" class="px-5 pt-4">
                            <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                Settings
                            </div>
                        </li>
                        <li v-else class="border-t border-gray-200 my-2"></li>

                        <!-- My Profile -->
                        <li v-if="user.data.staff">
                            <router-link
                                to="/myprofile"
                                :class="getMenuItemClass('/myprofile')"
                                :title="isCollapsed ? 'My Profile' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-user-circle text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">My Profile</span>
                            </router-link>
                        </li>

                        <!-- Reports -->
                        <li v-if="checkPermissionExist(user.data, 'report-read')">
                            <router-link
                                to="/reports"
                                :class="getMenuItemClass('/reports')"
                                :title="isCollapsed ? 'Reports' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bxs-report text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Reports</span>
                            </router-link>
                        </li>

                        <!-- Settings -->
                        <li>
                            <router-link
                                to="/settings/profile"
                                :class="getMenuItemClass('/settings/profile')"
                                :title="isCollapsed ? 'Settings' : ''"
                                @click.native="closeSidebarOnMobile"
                            >
                                <span :class="getIconClass">
                                    <i class="bx bx-cog text-xl"></i>
                                </span>
                                <span v-if="!isCollapsed" class="menu-text">Settings</span>
                            </router-link>
                        </li>
                    </ul>
                </div>

            <!-- Footer (Collapse hint for mobile) -->
            <div v-if="!isCollapsed" class="border-t border-gray-200 p-4 hidden lg:block">
                <div class="text-xs text-gray-500 text-center">
                    <i class="bx bx-chevron-left"></i> Click to collapse
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import checkPermissionExist from '@/mixins/checkPermissionExist';

export default {
    mixins: [checkPermissionExist],

    data() {
        return {
            windowWidth: window.innerWidth,
        };
    },

    computed: {
        ...mapGetters({
            user: 'auth/user',
            isSidebarOpen: 'ui/isSidebarOpen',
            isCollapsed: 'ui/isSidebarCollapsed',
        }),

        isMobile() {
            return this.windowWidth < 1024;
        },

        sidebarVisibilityClass() {
            // On mobile: show/hide based on isSidebarOpen
            // On desktop: always visible
            if (this.isMobile) {
                return this.isSidebarOpen ? 'translate-x-0' : '-translate-x-full';
            }
            return 'translate-x-0';
        },

        getIconClass() {
            return this.isCollapsed 
                ? 'mx-auto inline-flex justify-center items-center transition-all duration-200' 
                : 'ml-4 inline-flex justify-center items-center transition-all duration-200';
        },
    },

    methods: {
        ...mapActions({
            toggleSidebar: 'ui/toggleSidebar',
            setSidebarCollapsed: 'ui/setSidebarCollapsed',
        }),

        handleToggleClick() {
            // On mobile, close the sidebar; on desktop, collapse it
            if (window.innerWidth < 1024) {
                this.toggleSidebar(false);
            } else {
                this.setSidebarCollapsed(!this.isCollapsed);
            }
        },

        toggleCollapse() {
            this.setSidebarCollapsed(!this.isCollapsed);
        },

        closeSidebarOnMobile() {
            // Close sidebar on mobile when a menu item is clicked
            if (window.innerWidth < 1024) {
                this.toggleSidebar(false);
            }
        },

        getMenuItemClass(path) {
            const isActive = this.$route.fullPath === path || this.$route.fullPath.startsWith(path + '/');
            return [
                'group relative flex flex-row items-center h-12 focus:outline-none transition-all duration-200 mx-2 rounded-lg',
                isActive
                    ? 'bg-indigo-50 text-indigo-600 shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-indigo-600',
                this.isCollapsed ? 'justify-center px-2' : 'px-4'
            ];
        },

        handleResize() {
            this.windowWidth = window.innerWidth;
        },
    },

    mounted() {
        window.addEventListener('resize', this.handleResize);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    },
};
</script>

<style scoped>
/* Smooth transitions */
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter,
.slide-leave-to {
    transform: translateX(-100%);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* Custom scrollbar */
.custom-scrollbar::-webkit-scrollbar {
    width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 2px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}

/* Menu text animation */
.menu-text {
    margin-left: 0.75rem;
    font-size: 0.875rem;
    font-weight: 500;
    white-space: nowrap;
    transition: opacity 0.2s ease;
}

/* Tooltip for collapsed state */
[title]:hover::after {
    content: attr(title);
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 12px;
    padding: 8px 12px;
    background-color: #1f2937;
    color: white;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    z-index: 1000;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    animation: tooltipFadeIn 0.2s ease-in-out;
    pointer-events: none;
}

[title]:hover::before {
    content: '';
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 6px;
    border: 6px solid transparent;
    border-right-color: #1f2937;
    z-index: 1000;
    animation: tooltipFadeIn 0.2s ease-in-out;
    pointer-events: none;
}

@keyframes tooltipFadeIn {
    from {
        opacity: 0;
        transform: translateY(-50%) translateX(-4px);
    }
    to {
        opacity: 1;
        transform: translateY(-50%) translateX(0);
    }
}

@media print {
    body * {
        visibility: hidden;
    }

    #sidebar-container,
    #sidebar-container * {
        visibility: hidden;
    }

    .print__hide {
        display: none;
    }
}
</style>
